import { _keys, debounce, deepClone, merge, Module } from '@nu-art/ts-common';
import { AdvisorPadding, ColumnKeys, defaultAdvisorBoard } from '@app/advisor-v5-shared';
import { dispatch_onAdvisorBoardUpdated } from './dispatchers.js';
class ModuleFE_AdvisorBoard_Class extends Module {
    advisorWindow = { width: 0, height: 0 };
    advisorBoard = defaultAdvisorBoard;
    init() {
        super.init();
        this.advisorBoard = deepClone(defaultAdvisorBoard);
        this.initializeIframeDisplayedObserver();
    }
    __onWindowResized() {
        this.updateAdvisorMeasurements();
    }
    //######################### Internal #########################
    initializeIframeDisplayedObserver = () => {
        const observer = new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting)
                return;
            this.updateAdvisorMeasurements();
        }, {
            root: null, // Observe visibility relative to the parent viewport
            threshold: 0.01, // Trigger when at least 1% of the iframe is visible
        });
        // Observe the iframe's document body
        observer.observe(document.documentElement);
    };
    resolveEffectiveHeight = (columnItems, ratio) => {
        if (ratio > 1)
            return ratio;
        const actualItems = columnItems.filter(item => item.height > 0);
        //Calculate height without panels that have defined heights in PX
        const heightWithoutConstPanels = actualItems.reduce((height, panel) => {
            return panel.height <= 1 ? height : (height - panel.height);
        }, this.advisorWindow.height);
        //Calculate height without paddings
        const effectiveHeight = actualItems.reduce((size, columnItem, index) => {
            let padding = 16;
            if (index === 0)
                padding -= 8;
            if (index === actualItems.length - 1)
                padding -= 8;
            return size - padding;
        }, heightWithoutConstPanels);
        return Math.ceil(ratio * effectiveHeight);
    };
    resolveEffectiveWidth = (columnKey, ratio) => {
        const effectiveWidth = _keys(this.advisorBoard).reduce((size, currentColumnKey) => {
            if (columnKey === currentColumnKey || this.advisorBoard[columnKey].width <= 1)
                return size;
            return size - this.advisorBoard[columnKey].width;
        }, this.advisorWindow['width']);
        return Math.ceil(effectiveWidth * ratio);
    };
    onMeasurementUpdated = debounce(() => {
        dispatch_onAdvisorBoardUpdated.dispatchUI([...ColumnKeys]);
    }, 100, 500);
    //######################### Get #########################
    getColumnWidth = (columnKey) => {
        const ratio = this.advisorBoard[columnKey].width;
        // if the size is bigger than 1 it's in pixels, return it as is
        if (ratio > 1)
            return ratio;
        return this.resolveEffectiveWidth(columnKey, ratio);
    };
    getColumnItems = (columnKey) => {
        return deepClone(this.advisorBoard[columnKey].columnItems);
    };
    getItemHeight = (columnKey, panelKey) => {
        const items = this.advisorBoard[columnKey].columnItems;
        const itemToResolve = items.find(item => item.panelKey === panelKey);
        if (!itemToResolve) {
            this.logError(`cannot resolve panel with key ${panelKey} in column ${columnKey}`);
            return 0;
        }
        return this.resolveEffectiveHeight(items, itemToResolve.height);
    };
    getPanelItem = (panelKey) => {
        for (const key of _keys(this.advisorBoard)) {
            const item = this.advisorBoard[key].columnItems.find(item => item.panelKey === panelKey);
            if (item)
                return item;
        }
    };
    getAdvisorMeasures = () => {
        const pageAdvisor = document.getElementById('page-advisor');
        // Can't get the page ref
        if (!pageAdvisor) {
            this.logDebug('Could not find advisor html element');
            return { width: 0, height: 0 };
        }
        return { width: pageAdvisor.clientWidth, height: pageAdvisor.clientHeight - AdvisorPadding };
    };
    panelInColumn = (panelKey, columnKey) => {
        return !!this.getColumnItems(columnKey).find(panel => panel.panelKey === panelKey);
    };
    getBoard = () => {
        return deepClone(this.advisorBoard);
    };
    //######################### Set #########################
    resetAdvisorBoard = () => {
        this.advisorBoard = deepClone(defaultAdvisorBoard);
        dispatch_onAdvisorBoardUpdated.dispatchUI([...ColumnKeys]);
    };
    setColumnItems = (key, advisorColumnItems) => {
        const newBoard = deepClone(this.advisorBoard);
        newBoard[key].columnItems = advisorColumnItems;
        this.advisorBoard = newBoard;
        dispatch_onAdvisorBoardUpdated.dispatchUI([key]);
    };
    //######################### Update #########################
    updateColumnsWidth = (columnState) => {
        const affectedKeys = _keys(columnState);
        const changingKeys = affectedKeys.filter(key => columnState[key] !== this.advisorBoard[key]);
        if (!changingKeys.length)
            return;
        this.advisorBoard = merge(deepClone(this.advisorBoard), columnState, true);
        dispatch_onAdvisorBoardUpdated.dispatchUI(changingKeys);
    };
    updateAdvisorMeasurements = () => {
        this.advisorWindow = this.getAdvisorMeasures();
        this.onMeasurementUpdated();
    };
}
export const ModuleFE_AdvisorBoard = new ModuleFE_AdvisorBoard_Class();
//# sourceMappingURL=ModuleFE_AdvisorBoard.js.map